(() => {
  class ProductBundler extends HTMLElement {
    #mediaAbortController;
    #boundStickyClick;

    connectedCallback() {
      this.bundleProducts = this.#readBundleProducts();
      this.sizeInputs = Array.from(this.querySelectorAll('input[name="bundle-size"]'));
      this.optionGroups = this.#readOptionGroups();
      this.color1Inputs = Array.from(this.querySelectorAll('input[name="bundle-color-1"]'));
      this.color2Inputs = Array.from(this.querySelectorAll('input[name="bundle-color-2"]'));
      this.color1LegendName = this.querySelector('[data-bundle-color-1-name]');
      this.color2LegendName = this.querySelector('[data-bundle-color-2-name]');
      this.addToCartButton = this.querySelector('[data-bundle-add]');
      this.status = this.querySelector('[data-bundle-status]');
      this.form = this.querySelector('[data-bundle-form]');
      this.sectionRoot = this.closest('.product-bundler');
      this.mediaGrid = this.sectionRoot?.querySelector('[data-bundle-media-grid]') || null;

      if (!this.form || !this.addToCartButton) return;

      this.form.addEventListener('change', (event) => this.#handleFormChange(event));
      this.form.addEventListener('submit', (event) => this.#handleSubmit(event));

      this.#boundStickyClick = (e) => {
        if (!e.target.closest('[data-bundle-sticky-add]')) return;
        if (this.addToCartButton && !this.addToCartButton.disabled) {
          this.addToCartButton.click();
        }
      };
      document.addEventListener('click', this.#boundStickyClick);

      this.stickySizeSelect = document.querySelector('[data-sticky-bundle-size]');
      if (this.stickySizeSelect) {
        this.stickySizeSelect.addEventListener('change', (event) => {
          const selectedSize = event.target.value;
          const matchingSizeInput = this.sizeInputs.find((input) => input.value === selectedSize);
          if (matchingSizeInput && !matchingSizeInput.disabled) {
            matchingSizeInput.checked = true;
            this.form.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      this.#updateAvailability();
      const defaultPreviewId =
        this.#selectedColorProductId('bundle-color-1') || this.#selectedColorProductId('bundle-color-2');
      if (defaultPreviewId) {
        this.#updateMediaGallery(defaultPreviewId, this.#getSelection().optionValues);
      }

      this.#syncState();
    }

    disconnectedCallback() {
      if (this.#boundStickyClick) {
        document.removeEventListener('click', this.#boundStickyClick);
      }
    }

    #updateAvailability() {
      // A small stabilization loop handles cascading changes when a fallback size/color is auto-selected.
      for (let attempts = 0; attempts < 3; attempts += 1) {
        if (!this.#updateAvailabilityPass()) break;
      }
      this.#updateColorLegendText();
    }

    #updateColorLegendText() {
      const selectedColor1 = this.querySelector('input[name="bundle-color-1"]:checked');
      const selectedColor2 = this.querySelector('input[name="bundle-color-2"]:checked');

      if (this.color1LegendName) {
        this.color1LegendName.textContent = selectedColor1?.dataset?.colorLabel || '';
      }

      const stickyColor1 = document.querySelector('[data-sticky-color-1]');
      if (stickyColor1) {
        stickyColor1.textContent = selectedColor1?.dataset?.colorLabel || '';
      }

      if (this.color2LegendName) {
        this.color2LegendName.textContent = selectedColor2?.dataset?.colorLabel || '';
      }

      const stickyColor2 = document.querySelector('[data-sticky-color-2]');
      if (stickyColor2) {
        stickyColor2.textContent = selectedColor2?.dataset?.colorLabel || '';
      }
    }

    /**
     * Disables the option values and colours the rest of the selection cannot reach, and
     * reselects a usable value wherever the current one just became unavailable.
     * @returns {boolean} Whether anything was reselected, so the caller can run another pass.
     */
    #updateAvailabilityPass() {
      let changed = false;

      for (const group of this.optionGroups) {
        // Re-read per group so a value unchecked by an earlier group is already gone.
        const { color1ProductId, color2ProductId, optionValues } = this.#getSelection();
        const colorProductIds = [color1ProductId, color2ProductId].filter(Boolean);

        for (const input of group.inputs) {
          const candidate = { ...optionValues, [group.optionKey]: input.value };
          const isAvailable = colorProductIds.every((productId) => Boolean(this.#findVariant(productId, candidate)));

          input.disabled = !isAvailable;
          const label = input.closest('label');
          if (label) label.classList.toggle('unavailable', !isAvailable);

          if (!isAvailable && input.checked) {
            input.checked = false;
            changed = true;
          }
        }

        if (!group.inputs.some((input) => input.checked && !input.disabled)) {
          const firstAvailableValue = group.inputs.find((input) => !input.disabled);
          if (firstAvailableValue) {
            firstAvailableValue.checked = true;
            changed = true;
          }
        }
      }

      const { optionValues } = this.#getSelection();

      /** @param {HTMLInputElement[]} inputs - One colour slot's swatch inputs. */
      const updateColorGroup = (inputs) => {
        for (const input of inputs) {
          const isAvailable = Boolean(this.#findVariant(Number(input.value), optionValues));
          input.disabled = !isAvailable;

          const label = input.closest('label');
          if (label) {
            label.classList.toggle('unavailable', !isAvailable);
          }

          if (!isAvailable && input.checked) {
            input.checked = false;
            changed = true;
          }
        }

        const checkedColor = inputs.find((input) => input.checked && !input.disabled);
        if (!checkedColor) {
          const firstAvailableColor = inputs.find((input) => !input.disabled);
          if (firstAvailableColor) {
            firstAvailableColor.checked = true;
            changed = true;
          }
        }
      };

      updateColorGroup(this.color1Inputs);
      updateColorGroup(this.color2Inputs);

      return changed;
    }

    /**
     * @typedef {Object} OptionGroup
     * @property {string} inputName - The shared `name` of the group's radio inputs.
     * @property {string} optionKey - The option name, lowercased to match the product payload.
     * @property {HTMLInputElement[]} inputs - The group's radio inputs, in render order.
     */

    /**
     * Groups every rendered option radio (Size, Inseam, Liner, ...) by its input name.
     * @returns {OptionGroup[]} One entry per option the block rendered.
     */
    #readOptionGroups() {
      const groups = [];

      for (const input of this.querySelectorAll('input[data-option-name]')) {
        const existingGroup = groups.find((group) => group.inputName === input.name);
        if (existingGroup) {
          existingGroup.inputs.push(input);
          continue;
        }

        groups.push({
          inputName: input.name,
          optionKey: input.dataset.optionName.toLowerCase(),
          inputs: [input],
        });
      }

      return groups;
    }

    #readBundleProducts() {
      const source = this.querySelector('[data-bundle-products-json]');
      if (!source) return [];

      try {
        const parsed = JSON.parse(source.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Bundle JSON parsing failed', error);
        return [];
      }
    }

    #selectedColorProductId(groupName) {
      const input = this.querySelector(`input[name="${groupName}"]:checked`);
      return input ? Number(input.value) : null;
    }

    #findBundleProduct(productId) {
      return this.bundleProducts.find((item) => Number(item.id) === Number(productId)) || null;
    }

    #renderSimpleMediaGrid(media, fallbackTitle = '') {
      if (!this.mediaGrid) return;
      const mediaList = Array.isArray(media) ? media : [];
      if (!mediaList.length) return;

      this.mediaGrid.innerHTML = '';

      mediaList.slice(0, 4).forEach((item) => {
        const mediaItem = document.createElement('div');
        mediaItem.className = 'product-bundler__media-item';

        const image = document.createElement('img');
        image.src = item.src;
        image.alt = item.alt || fallbackTitle;
        image.loading = 'lazy';

        mediaItem.appendChild(image);
        this.mediaGrid.appendChild(mediaItem);
      });
    }

    /**
     * Swaps the product media for the colour being previewed.
     * @param {number} productId - The bundle product to preview.
     * @param {Object<string, string>} [optionValues] - Selected values, keyed by lowercased option name.
     * @returns {Promise<void>}
     */
    async #updateMediaGallery(productId, optionValues = {}) {
      const selectedProduct = this.#findBundleProduct(productId);
      if (!selectedProduct) return;

      const media = Array.isArray(selectedProduct.media) ? selectedProduct.media : [];
      if (!media.length) return;

      // Legacy fallback for the original custom bundler section.
      if (this.mediaGrid) {
        this.#renderSimpleMediaGrid(media, selectedProduct.title || '');
        return;
      }

      // Product-information layout uses native media-gallery. Replace it like normal PDP variant updates.
      const section = this.closest('.shopify-section');
      const currentGallery = section?.querySelector('media-gallery');
      if (!currentGallery || !selectedProduct.url) return;

      const variant = this.#findVariant(productId, optionValues);
      const requestUrl = new URL(selectedProduct.url, window.location.origin);
      if (variant?.id) requestUrl.searchParams.set('variant', String(variant.id));

      try {
        this.#mediaAbortController?.abort();
        this.#mediaAbortController = new AbortController();

        const response = await fetch(requestUrl.toString(), { signal: this.#mediaAbortController.signal });
        if (!response.ok) return;

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const nextGallery = html.querySelector('media-gallery');
        if (nextGallery) currentGallery.replaceWith(nextGallery);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('Bundle gallery update failed', error);
        }
      }
    }

    #handleFormChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) {
        this.#syncState();
        return;
      }

      this.#updateAvailability();
      const selection = this.#getSelection();
      let previewId = selection.color1ProductId || selection.color2ProductId;
      if (target.name === 'bundle-color-1') {
        previewId = selection.color1ProductId;
      } else if (target.name === 'bundle-color-2') {
        previewId = selection.color2ProductId;
      }
      if (previewId) this.#updateMediaGallery(previewId, selection.optionValues);

      this.#syncState();
    }

    /**
     * Finds the first available variant of a bundle product matching the selected options.
     * @param {number} productId - The bundle product to search.
     * @param {Object<string, string>} [optionValues] - Selected values, keyed by lowercased option name.
     * @returns {Object|null} The matching variant, or null when none is available.
     */
    #findVariant(productId, optionValues = {}) {
      const selectedProduct = this.#findBundleProduct(productId);
      if (!selectedProduct) return null;

      const variants = Array.isArray(selectedProduct.variants) ? selectedProduct.variants : [];
      if (!variants.length) return null;

      const optionNames = Array.isArray(selectedProduct.options_with_values)
        ? selectedProduct.options_with_values.map((option) => String(option.name).toLowerCase())
        : [];

      // Only options this product actually carries can constrain the match, so a bundle mixing
      // products with and without an Inseam option still resolves for both.
      const constraints = optionNames
        .map((name, index) => [index, optionValues[name]])
        .filter(([, value]) => Boolean(value));

      if (!constraints.length) {
        return variants.find((variant) => variant.available) || null;
      }

      return (
        variants.find((variant) => {
          if (!variant.available) return false;
          const values = Array.isArray(variant.options) ? variant.options : [];
          return constraints.every(([index, value]) => values[index] === value);
        }) || null
      );
    }

    #setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.classList.toggle('bundle-form__status--error', isError);
      this.status.classList.toggle('bundle-selector__status--error', isError);
    }

    /**
     * Reads the current bundle selection: every option value, both colours and their variants.
     * @returns {{optionValues: Object<string, string>, sizeValue: string, color1ProductId: number|null,
     *   color2ProductId: number|null, variant1: Object|null, variant2: Object|null}} The current selection.
     */
    #getSelection() {
      const optionValues = Object.create(null);
      for (const group of this.optionGroups) {
        const checkedValue = group.inputs.find((input) => input.checked);
        if (checkedValue) optionValues[group.optionKey] = checkedValue.value;
      }

      const selectedSize = this.querySelector('input[name="bundle-size"]:checked');
      const sizeValue = selectedSize ? selectedSize.value : '';
      const color1ProductId = this.#selectedColorProductId('bundle-color-1');
      const color2ProductId = this.#selectedColorProductId('bundle-color-2');

      return {
        optionValues,
        sizeValue,
        color1ProductId,
        color2ProductId,
        variant1: color1ProductId ? this.#findVariant(color1ProductId, optionValues) : null,
        variant2: color2ProductId ? this.#findVariant(color2ProductId, optionValues) : null,
      };
    }

    #getStickyButton() {
      return document.querySelector('[data-bundle-sticky-add]');
    }

    #syncState() {
      if (!this.addToCartButton) return;
      this.#updateAvailability();

      const selection = this.#getSelection();
      const isValid = Boolean(selection.variant1 && selection.variant2);

      this.addToCartButton.disabled = !isValid;
      this.addToCartButton.textContent = isValid ? 'Add To Cart' : 'Select options and colors';

      const stickyBtn = this.#getStickyButton();
      if (stickyBtn) {
        stickyBtn.disabled = !isValid;
        stickyBtn.textContent = isValid ? 'Add To Cart' : 'Select options and colors';
      }

      const stickySizeSelect = document.querySelector('[data-sticky-bundle-size]');
      if (stickySizeSelect && selection.sizeValue) {
        stickySizeSelect.value = selection.sizeValue;

        const customValueDisplay = document.querySelector('[data-custom-select-value]');
        if (customValueDisplay) {
          const selectedOption = stickySizeSelect.querySelector(`option[value="${selection.sizeValue}"]`);
          if (selectedOption) customValueDisplay.textContent = selectedOption.textContent;
        }
      }

      if (
        selection.color1ProductId &&
        selection.color2ProductId &&
        (!selection.variant1 || !selection.variant2)
      ) {
        this.#setStatus('Selected size is unavailable for one of the colors.', true);
        return;
      }

      this.#setStatus('');
    }

    async #handleSubmit(event) {
      event.preventDefault();

      if (!this.addToCartButton || this.addToCartButton.disabled) return;

      const selection = this.#getSelection();
      if (!selection.variant1 || !selection.variant2) {
        this.#setStatus('Please select size and both colors.', true);
        return;
      }

      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent = 'Adding...';
      const stickyBtn = this.#getStickyButton();
      if (stickyBtn) {
        stickyBtn.disabled = true;
        stickyBtn.textContent = 'Adding...';
      }
      this.#setStatus('');

      try {
        const sections = Array.from(document.querySelectorAll('cart-items-component'))
          .map((element) => element.dataset.sectionId)
          .filter(Boolean);

        const bundleId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/x/g, () =>
                Math.floor(Math.random() * 16).toString(16)
              );
        const bundleDiscountPercentage = this.dataset.bundleDiscountPercentage || '';
        const bundleTitle = this.dataset.bundleTitle || '';

        const bundleProperties = {
          _bundle_id: bundleId,
          _bundle_size: '2',
          _bundle_discount_percentage: String(bundleDiscountPercentage),
          _bundle_title: bundleTitle,
        };

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(selection.variant1.id),
                quantity: 1,
                properties: bundleProperties,
              },
              {
                id: Number(selection.variant2.id),
                quantity: 1,
                properties: bundleProperties,
              },
            ],
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });

        const payload = await response.json();

        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'Unable to add bundle.');
        }

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: payload,
              sourceId: `bundle-${this.dataset.sectionId || 'product'}`,
              data: {
                source: 'product-form-component',
                itemCount: 2,
                sections: payload.sections || {},
              },
            },
          })
        );

        this.addToCartButton.textContent = 'Added';
        if (stickyBtn) stickyBtn.textContent = 'Added';
        window.setTimeout(() => this.#syncState(), 800);
      } catch (error) {
        this.#setStatus(error.message || 'Something went wrong. Please try again.', true);
        this.#syncState();
      }
    }
  }

  if (!customElements.get('product-bundler')) {
    customElements.define('product-bundler', ProductBundler);
  }
})();
